import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Save, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../../ui/button";
import { Alert, AlertDescription } from "../../../ui/alert";
import { payrollAPI } from "../../../../services/api";
import { usePayrollUIConfig } from "../../../../hooks/usePayrollUIConfig";
import TunisiaWorkingTimeCard from "./TunisiaWorkingTimeCard";
import TunisiaOvertimeCard from "./TunisiaOvertimeCard";
import TunisiaCnssCard from "./TunisiaCnssCard";
import TunisiaBonusesCard from "./TunisiaBonusesCard";
import TunisiaAccountingCard from "./TunisiaAccountingCard";

const TunisiaPayrollForm = () => {
  const { t } = useTranslation();
  const { config } = usePayrollUIConfig();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    expected_work_hours_per_month: config?.workingTime?.hoursPerMonth || 173.33,
    monthly_work_days: config?.workingTime?.daysPerMonth || 21.67,
    weekly_work_hours: 40,
    daily_work_hours: 8,
    saturday_working: true,
    overtime_multiplier_default: config?.overtimeMultipliers?.normal || 1.25,
    night_overtime_multiplier: config?.overtimeMultipliers?.night || 1.50,
    weekend_overtime_multiplier: config?.overtimeMultipliers?.weekend || 2.00,
    holiday_overtime_multiplier: config?.overtimeMultipliers?.holiday || 2.50,
    night_hours_start: "22:00",
    night_hours_end: "06:00",
    night_differential_rate: 0.15,
    annual_leave_days: 26,
    sick_leave_days: 15,
    maternity_leave_days: 98,
    paternity_leave_days: 3,
    absence_deduction_policy: config?.deductionPolicies?.absence || "proportional_hourly",
    absence_deduction_rate: config?.deductionPolicies?.absenceRate || 1.0,
    unpaid_absence_after_days: 3,
    lateness_deduction_policy: config?.deductionPolicies?.lateness || "after_tolerance",
    lateness_tolerance_minutes: 15,
    lateness_deduction_rate: config?.deductionPolicies?.latenessRate || 0.02,
    max_lateness_deduction_percent: config?.deductionPolicies?.maxLatenessPercent || 10.0,
    cnss_employee_rate: 0.0935,
    cnss_employer_rate: 0.229,
    enable_cnss_calculation: true,
    cnss_salary_cap: 6000,
    enable_irpp_calculation: true,
    irpp_method: "progressive",
    personal_allowance: 1500,
    enable_13th_month: false,
    enable_eid_bonus: true,
    eid_bonus_amount: 200,
    require_manager_approval: true,
    require_hr_approval: true,
    require_finance_approval: false,
    export_format: "csv",
    export_filename_template: "paie_tunisie_{period_start}_{period_end}.csv",
    accounting_integration: "sage",
    gl_salary_account: "6411",
    gl_payable_account: "4281",
    gl_cnss_account: "4291",
  });

  useEffect(() => { loadSettings(); }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const data = await payrollAPI.getPayrollSettings();
      if (data) setFormData(prev => ({ ...prev, ...data }));
    } catch (error) {
      toast.error(t("payrollTN.loadError"));
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      toast.info(t("payrollTN.savingToast"));
      await payrollAPI.updatePayrollSettings({ ...formData, country_code: 'TN' });
      toast.success(t("payrollTN.saveSuccess"));
      await loadSettings();
    } catch (error) {
      toast.error(t("payrollTN.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            🇹🇳 {t("payrollTN.title")}
          </h3>
          <p className="text-sm text-muted-foreground">
            {t("payrollTN.subtitle")}
          </p>
        </div>
        <Button onClick={handleSaveSettings} disabled={saving} className="flex items-center gap-2">
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current" />
              {t("payrollTN.savingBtn")}
            </>
          ) : (
            <>
              <Save className="h-4 w-4" />
              {t("payrollTN.saveBtn")}
            </>
          )}
        </Button>
      </div>

      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          {t("payrollTN.alertNote")}
        </AlertDescription>
      </Alert>

      <TunisiaWorkingTimeCard formData={formData} handleInputChange={handleInputChange} />
      <TunisiaOvertimeCard formData={formData} handleInputChange={handleInputChange} />
      <TunisiaCnssCard formData={formData} handleInputChange={handleInputChange} />
      <TunisiaBonusesCard formData={formData} handleInputChange={handleInputChange} />
      <TunisiaAccountingCard formData={formData} handleInputChange={handleInputChange} />
    </div>
  );
};

export default TunisiaPayrollForm;
